/**
 * Service detail URLs: `/services/{slugified-name}-{serviceId}`
 * `serviceId` is the API UUID (or legacy numeric id).
 */

const UUID_ONLY =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_AT_END =
  /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

export function slugifyServiceName(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[{},()[\]]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

/** Path segment only (no leading /services/). */
export function buildServiceSlugSegment(name: string, id: string | number): string {
  const idStr = String(id).trim();
  const slug = slugifyServiceName(name);
  if (!slug) return idStr;
  return `${slug}-${idStr}`;
}

export function buildServiceDetailPath(name: string, id: string | number, locale: string): string {
  const seg = buildServiceSlugSegment(name, id);
  return locale === "ar" ? `/service-detail/${seg}` : `/en/service-detail/${seg}`;
}

export function buildPackageDetailPath(name: string, id: string | number, locale: string): string {
  const seg = buildServiceSlugSegment(name, id);
  return locale === "ar" ? `/package-detail/${seg}` : `/en/package-detail/${seg}`;
}

export function buildProductDetailPath(name: string, id: string | number, locale: string): string {
  const seg = buildServiceSlugSegment(name, id);
  return locale === "ar" ? `/product-detail/${seg}` : `/en/product-detail/${seg}`;
}

export function buildOfferDetailPath(name: string, id: string | number, locale: string, slug?: string | null): string {
  const seg = slug !== undefined && slug !== null && slug.trim() !== "" ? slug : buildServiceSlugSegment(name, id);
  return locale === "ar" ? `/offer-detail/${seg}` : `/en/offer-detail/${seg}`;
}

/**
 * News detail path segment: prefer CMS `newsSlug` as-is when it is not a bare UUID
 * (API already returns SEO slugs; prefixing slugified title duplicates the segment).
 * For UUID-only identifiers, keep `title-uuid` for readable URLs and legacy links.
 */
export function buildNewsDetailSlugSegment(title: string, apiSlug: string): string {
  const id = String(apiSlug).trim();
  if (!id) {
    const slug = slugifyServiceName(title);
    return slug || "news";
  }
  if (UUID_ONLY.test(id)) {
    const slug = slugifyServiceName(title);
    if (!slug) return id;
    return `${slug}-${id}`;
  }
  return id;
}

export function buildNewsDetailPath(title: string, apiSlug: string, locale: string): string {
  return locale === "ar" ? `/news-detail/${apiSlug}` : `/en/news-detail/${apiSlug}`;
}

/**
 * Resolve the slug/id passed to OpenSearch news-details: prefers trailing UUID / numeric id / full slug.
 */
export function parseNewsDetailSlugParam(param: string): string {
  const trimmed = param.trim();
  const fromComposite = parseServiceSlugParam(trimmed);
  return fromComposite ?? trimmed;
}

/**
 * Resolve service id from a dynamic route param (slug-name-id, plain UUID, or legacy id).
 */
export function parseServiceSlugParam(param: string): string | null {
  const decoded = decodeURIComponent(param).trim();
  if (!decoded) return null;

  if (UUID_ONLY.test(decoded)) return decoded;

  const um = decoded.match(UUID_AT_END);
  if (um) return um[1];

  if (/^\d+$/.test(decoded)) return decoded;

  const nm = decoded.match(/^.*-(\d+)$/);
  if (nm) return nm[1];

  return null;
}

/**
 * Resolve an offer's identifier from a dynamic route param.
 *
 * Differs from {@link parseServiceSlugParam}: when no UUID/numeric id is found,
 * we return the decoded string itself so the offer API can be looked up by slug
 * (e.g. SEO URLs like `/offer-detail/free-oil-change-promo`).
 *
 * Returns:
 *  - `{ kind: "uuid", value }` when a UUID is present (plain UUID or `name-uuid`)
 *  - `{ kind: "slug", value }` when only a non-empty slug string is present
 *  - `null` when the param is empty
 */
export function parseOfferSlugParam(
  param: string,
): { kind: "uuid"; value: string } | { kind: "slug"; value: string } | null {
  const decoded = decodeURIComponent(param).trim();
  if (!decoded) return null;

  if (UUID_ONLY.test(decoded)) return { kind: "uuid", value: decoded };

  const um = decoded.match(UUID_AT_END);
  if (um) return { kind: "uuid", value: um[1] };

  return { kind: "slug", value: decoded };
}

export function serviceIdsEqual(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}
